import type { AgentBeat, Beat, ReplayFrame } from "../types";

export type WeaveFilter = "all" | "near" | "off";

export interface WovenBeat {
  tick: number;
  text: string;
  source: "agent" | "settlement";
  kind?: string;
}

/** Her node at a 1-based tick; past her last frame she lies where she fell. */
export function agentNodeAt(frames: ReplayFrame[], agent: number, tick: number): number {
  const index = Math.min(Math.max(tick - 1, 0), frames.length - 1);
  return frames[index].positions[agent];
}

/** Chebyshev distance ≤ 1 on a `width`-wide grid; nodeless beats are never near. */
export function nearHer(
  beatNode: number | null | undefined,
  herNode: number,
  width: number,
): boolean {
  if (beatNode === null || beatNode === undefined) return false;
  const dx = Math.abs((beatNode % width) - (herNode % width));
  const dy = Math.abs(Math.floor(beatNode / width) - Math.floor(herNode / width));
  return dx <= 1 && dy <= 1;
}

/**
 * One chronological stream: her beats bright, the settlement's woven in dimmed.
 * Ties at a tick put settlement first — cause before effect. Sort stability does the
 * work: settlement entries enter the array first.
 */
export function weaveBeats(
  agentBeats: AgentBeat[],
  settlementBeats: Beat[],
  filter: WeaveFilter,
  frames: ReplayFrame[],
  agent: number,
  width: number,
): WovenBeat[] {
  const settlement =
    filter === "off"
      ? []
      : settlementBeats.filter(
          (beat) =>
            filter === "all" ||
            nearHer(beat.node, agentNodeAt(frames, agent, beat.tick), width),
        );
  const woven: WovenBeat[] = [
    ...settlement.map((b) => ({ tick: b.tick, text: b.text, source: "settlement" as const })),
    ...agentBeats.map((b) => ({
      tick: b.tick,
      text: b.text,
      source: "agent" as const,
      kind: b.kind,
    })),
  ];
  return woven.sort((a, b) => a.tick - b.tick);
}
